/**
 * Right column: the evidence behind the chart, as text.
 *
 * Two jobs. It explains what the chart is asserting — which brokers the
 * cluster selected, how ownership is shifting — and it is the non-canvas
 * reading of those numbers, so the workspace is usable when the chart is not.
 *
 * The net-value profile is deliberately NOT listed here: eighteen price bins
 * as text pushed everything else off the panel, and the chart's own strip
 * already reads them at a glance. Screener results are not here either —
 * they have their own view in the workspace, and a criterion echoed in two
 * places is one place too many.
 */
import type { WebChart } from "../domain/chart.ts";
import { formatMonth, formatPercent, NO_VALUE } from "../domain/format.ts";

function last<T>(values: readonly T[]): T | undefined {
	return values[values.length - 1];
}

function HoldingList({ chart }: { chart: WebChart }) {
	const holding = chart.holding_composition;
	if (!holding) return <p className="evidence__empty">No holding composition for this instrument.</p>;

	const index = holding.periods.length - 1;
	const previous = index - 1;
	const rows = [
		["Foreign", holding.foreign],
		["Local institutional", holding.local_institutional],
		["Local individual", holding.local_individual],
	] as const;

	return (
		<>
			<p className="evidence__caption">
				{formatMonth(last(holding.periods))} · scripless {formatPercent(last(holding.scripless_ratio))}
			</p>
			<table className="table">
				<thead>
					<tr>
						<th scope="col">Holder</th>
						<th scope="col">Share</th>
						<th scope="col">MoM</th>
					</tr>
				</thead>
				<tbody>
					{rows.map(([label, values]) => {
						const current = values[index] ?? null;
						const prior = previous >= 0 ? values[previous] ?? null : null;
						const change = current !== null && prior !== null ? current - prior : null;
						return (
							<tr key={label}>
								<th scope="row">{label}</th>
								<td>{formatPercent(current)}</td>
								<td data-tone={change === null ? undefined : change < 0 ? "down" : "up"}>
									{change === null ? NO_VALUE : `${change >= 0 ? "+" : ""}${(change * 100).toFixed(2)}pp`}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</>
	);
}

function ClusteringCard({ chart }: { chart: WebChart }) {
	const clustering = chart.annotations.clustering;
	if (!clustering) {
		return <p className="evidence__empty">Foreign flow uses reported foreign buy/sell, so there is no broker cluster.</p>;
	}
	return (
		<>
			<dl className="pairs">
				<div>
					<dt>Clusters selected</dt>
					<dd>{clustering.n_selected_cluster ?? NO_VALUE}</dd>
				</div>
				<div>
					<dt>Cluster correlation</dt>
					<dd>{formatPercent(clustering.correlation)}</dd>
				</div>
			</dl>
			<ul className="chips" aria-label="Selected brokers">
				{clustering.selected_brokers.map((broker) => (
					<li key={broker} className="chip">
						{broker.toUpperCase()}
					</li>
				))}
			</ul>
		</>
	);
}

interface EvidencePanelProps {
	chart: WebChart;
	/** Collapsed to a narrow strip, giving its width to the chart. */
	collapsed?: boolean;
	/** Absent means the panel cannot be collapsed at all. */
	onToggleCollapsed?: () => void;
}

/**
 * Always rendered, at every width.
 *
 * It used to collapse, defaulting to closed below 1280px — which on a phone
 * put the holding composition and the profile bins behind a control most
 * people never found, below the fold. The panel is the text reading of the
 * chart; hiding it by default hid the evidence.
 */
export function EvidencePanel({ chart, collapsed = false, onToggleCollapsed }: EvidencePanelProps) {
	return (
		<aside className="evidence" data-collapsed={collapsed ? "true" : undefined} aria-labelledby="details-heading">
			<div className="evidence__head">
				<h2 id="details-heading">Details</h2>
				{/* The body is hidden, never unmounted: aria-controls keeps
				    resolving, and the collapsed panel stays a real strip with a
				    real control rather than a dead 0px column. */}
				{onToggleCollapsed ? (
					<button
						type="button"
						className="panel-toggle evidence__collapse"
						aria-expanded={!collapsed}
						aria-controls="details-body"
						title={collapsed ? "Expand Details" : "Collapse Details"}
						onClick={onToggleCollapsed}
					>
						<span aria-hidden="true">{collapsed ? "«" : "»"}</span>
						<span className="visually-hidden">{collapsed ? "Expand Details" : "Collapse Details"}</span>
					</button>
				) : null}
			</div>

			<div id="details-body" className="evidence__body" hidden={collapsed}>
				<section className="evidence__section">
					<h3>Broker clustering</h3>
					<ClusteringCard chart={chart} />
				</section>

				<section className="evidence__section">
					<h3>Holding composition</h3>
					<HoldingList chart={chart} />
				</section>

				<section className="evidence__section">
					<h3>Provenance</h3>
					<dl className="pairs">
						<div>
							<dt>Method</dt>
							<dd>{chart.annotations.method_label}</dd>
						</div>
						<div>
							<dt>Source</dt>
							<dd>{chart.meta.source}</dd>
						</div>
						<div>
							<dt>Generated</dt>
							<dd>{chart.meta.generated_at.replace("T", " ").slice(0, 19)}</dd>
						</div>
						<div>
							<dt>Schema</dt>
							<dd>v{chart.schema_version}</dd>
						</div>
					</dl>
				</section>
			</div>
		</aside>
	);
}
